import { Components, SimplePaletteColorOptions, Theme } from '@mui/material/styles';
import {
  black,
  black1,
  blueActive,
  blueDefault,
  blueHover,
  grayActive,
  grayDefault,
  grayHover,
  greenActive,
  greenDefault,
  greenHover,
  grey10,
  grey6,
  grey9,
  outlineActive,
  outlineHover,
  redActive,
  redDefault,
  redHover,
  white,
  yellowActive,
  yellowDefault,
  yellowHover,
} from './colors';
import { themePalette } from './themeColors';

declare module '@mui/material/styles' {
  interface Palette {
    normal: SimplePaletteColorOptions;
    tertiary: Palette['primary'];
  }

  interface PaletteOptions {
    normal?: SimplePaletteColorOptions;
    tertiary?: PaletteOptions['primary'];
  }
}
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    tertiary: true;
    link: true;
  }

  interface ButtonClasses {
    containedTertiary: string;
    outlinedTertiary: string;
    textTertiary: string;

    containedLink: string;
    outlinedLink: string;
    textLink: string;
  }
}

const oldButtonStyles: Components<Theme> = {
  MuiButton: {
    defaultProps: {
      variant: 'contained',
      disableElevation: true,
      size: 'large',
      color: 'primary',
    },
    styleOverrides: {
      root: {
        padding: '8px 16px',
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: '16px',
        lineHeight: '22px',
        textTransform: 'unset',
        borderRadius: '8px',
        fontFamily: 'Yelo',
        gap: '8px',
        color: black1,

        '& .MuiSvgIcon-root': {
          height: '22px',
          width: '22px',
        },

        '& .MuiButton-startIcon': {
          marginRight: '10px',
        },

        '& .MuiButton-endIcon': {
          marginLeft: '10px',
        },

        '&.MuiButton-containedSizeSmall': {
          padding: '8px 8px',
          fontSize: '12px !important',
          lineHeight: '16px',

          '& .MuiSvgIcon-root': {
            height: '16px',
            width: '16px',
          },
        },

        '&.MuiButton-containedSizeMedium': {
          padding: '8px 12px',
          fontSize: '14px',
          lineHeight: '20px',
          gap: '8px',
          '& .MuiSvgIcon-root': {
            height: '18px',
            width: '18px',
          },
        },

        '&.MuiButton-containedSizeLarge': {
          padding: '8px 16px',
          fontSize: '16px',
          lineHeight: '24px',

          '& .MuiSvgIcon-root': {
            height: '20px',
            width: '20px',
          },
        },

        '&:disabled': {
          color: white,
          backgroundColor: grey6,
        },
      },
      textPrimary: {
        color: black,
        backgroundColor: 'transparent',
        fontWeight: '600',
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
      outlinedPrimary: {
        color: black,
        backgroundColor: 'transparent',
        borderWidth: '1px',
        borderColor: '#14191F33',
        '&:hover': {
          background: outlineHover,
        },
        '&:active': {
          background: outlineActive,
          // background: outlineActive,
        },
      },
      containedError: {
        color: white,
        background: redDefault,
        '&:hover': {
          background: redHover,
        },
        '&:active': {
          backgroundColor: redActive,
        },
      },
      containedPrimary: {
        color: black1,
        backgroundColor: yellowDefault,
        '&:hover': {
          backgroundColor: yellowHover,
        },
        '&:active': {
          backgroundColor: yellowActive,
        },
      },
      outlinedSecondary: {
        color: black,
        backgroundColor: 'transparent',
        borderWidth: '0px',
        border: 0,
        '&:hover': {
          backgroundColor: outlineHover,
          border: 0,
        },
        '&:active': {
          background: outlineActive,
          border: 0,
        },
      },
      containedSecondary: {
        color: black,
        backgroundColor: grayDefault,
        // padding: '6px 18px',
        '&:hover': {
          backgroundColor: grayHover,
        },
        '&:active': {
          backgroundColor: grayActive,
        },
      },
      contained: {
        color: black1,
        background: grey10,
        '&:hover': {
          background: grey9,
        },
      },
      containedTertiary: {
        color: black1,
        background: themePalette.default.primary_background,
        '&:hover': {
          background: grey9,
        },
      },
      containedInfo: {
        color: white,
        background: blueDefault,
        fontWeight: 400,
        '&:hover': {
          background: blueHover,
        },
        '&:active': {
          backgroundColor: blueActive,
        },
        minWidth: '0px',
      },
      containedSuccess: {
        color: white,
        background: greenDefault,
        fontWeight: 400,
        '&:hover': {
          background: greenHover,
        },
        '&:active': {
          backgroundColor: greenActive,
        },
        minWidth: '0px',
      },
      containedWarning: {
        color: white,
        background: redDefault,
        '&:hover': {
          background: redHover,
        },
        '&:active': {
          backgroundColor: redActive,
        },
      },
    },
  },
};

const buttonStyles: Components<Theme> = {
  MuiButton: {
    defaultProps: {
      variant: 'contained',
      disableElevation: true,
      size: 'large',
      color: 'primary',
    },
    styleOverrides: {
      root: {
        padding: '16px',
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: '16px',
        lineHeight: '24px',
        borderRadius: '10px',
        textTransform: 'unset',
        color: themePalette.default.element_brand,

        '&.MuiButton-containedSizeSmall': {
          padding: '8px 16px',
        },
      },

      // containedPrimary: {
      //   color: black,
      //   backgroundColor: themePalette.default.element_brand,
      //   '&:hover': {
      //     backgroundColor: themePalette.hover.brand_hover,
      //   },
      //   '&:active': {
      //     backgroundColor: themePalette.pressed.brand_pressed,
      //   },
      //   '&:disabled': {
      //     backgroundColor: themePalette.inactive.brand_inactive,
      //     color: themePalette.text_and_icon.third_party,
      //   },
      // },
      containedSecondary: {
        color: themePalette.text_and_icon.primary,
        backgroundColor: themePalette.default.primary_background,
        '&:hover': {
          backgroundColor: themePalette.hover.primary_hover,
        },
        '&:active': {
          backgroundColor: themePalette.pressed.primary_pressed,
        },
        '&:disabled': {
          backgroundColor: themePalette.inactive.primary_inactive,
          color: themePalette.text_and_icon.third_party,
        },
      },
      containedTertiary: {
        color: themePalette.text_and_icon.nested,
        backgroundColor: themePalette.default.element_black,
        '&:hover': {
          backgroundColor: themePalette.hover.black_hover,
        },
        '&:active': {
          backgroundColor: themePalette.pressed.black_pressed,
        },
        '&:disabled': {
          backgroundColor: themePalette.inactive.black_inactive,
          color: themePalette.text_and_icon.third_party,
        },
      },
      containedLink: {
        color: themePalette.text_and_icon.primary,
        background: 'transparent',
        '&:hover': {
          backgroundColor: themePalette.hover.primary_hover,
        },
        '&:active': {
          backgroundColor: themePalette.pressed.primary_pressed,
        },
        '&:disabled': {
          color: themePalette.text_and_icon.third_party,
        },
      },
      contained: {
        color: black,
        backgroundColor: themePalette.default.element_brand,
        '&:hover': {
          backgroundColor: themePalette.hover.brand_hover,
        },
        '&:active': {
          backgroundColor: themePalette.pressed.brand_pressed,
        },
        '&:disabled': {
          backgroundColor: themePalette.inactive.brand_inactive,
          color: themePalette.text_and_icon.third_party,
        },
      },
    },
  },
};

// export const buttonStyle = import.meta.env.DEV ? buttonStyles : oldButtonStyles;
export const buttonStyle = oldButtonStyles;
